import { Injectable, inject } from '@angular/core';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ChatMessageItem } from './client-connect-chat.service';
import { MarketFeathersService } from './market-feathers.service';

export interface SupportChatDialog {
  type: 'support';
  supportUserId: number;
}

export interface SupportChatStats {
  unread: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class SupportChatService {
  private readonly feathers = inject(MarketFeathersService);

  private dialog: SupportChatDialog | null = null;

  open(dialog: SupportChatDialog): void {
    this.dialog = {
      type: 'support',
      supportUserId: Number(dialog.supportUserId),
    };
  }

  close(): void {
    this.dialog = null;
  }

  get activeDialog(): SupportChatDialog | null {
    return this.dialog;
  }

  getSupportUserId(): Observable<number> {
    return from(this.feathers.service('/user-support').find({}) as Promise<{ userId?: number }>).pipe(
      map((res) => Number(res?.userId)),
      switchMap((userId) => {
        if (!Number.isFinite(userId) || userId <= 0) {
          return throwError(() => new Error('Support user is not available'));
        }
        return of(userId);
      })
    );
  }

  getStats(currentUserId: number | null): Observable<SupportChatStats> {
    return this.getSupportUserId().pipe(
      switchMap((supportUserId) => this.findMessages(supportUserId, 0, 50)),
      map((items) => {
        const last = items[items.length - 1];
        const unread = items.filter((msg) => this.isUnreadIncoming(msg, currentUserId)).length;
        return {
          unread,
          lastMessage: last?.text || (last ? 'Изображение' : null),
          lastMessageAt: last?.createdAt ?? null,
        };
      }),
      catchError(() => of({ unread: 0, lastMessage: null, lastMessageAt: null }))
    );
  }

  getMessages(page = 0, limit = 50): Observable<ChatMessageItem[]> {
    const dialog = this.requireDialog();
    return this.findMessages(dialog.supportUserId, page, limit);
  }

  sendMessage(text: string, quote?: ChatMessageItem | null): Observable<ChatMessageItem> {
    const dialog = this.requireDialog();
    const trimmed = text?.trim() ?? '';
    if (!trimmed && !quote) {
      return throwError(() => new Error('Empty message'));
    }

    const quoteMessages = quote ? [quote] : [];

    return this.createMessageLinks(quoteMessages).pipe(
      switchMap((messages) =>
        from(
          this.usersChat().create({
            text: trimmed,
            attached: { messages, uploads: [] },
            replyUserId: dialog.supportUserId,
            type: 'support',
            typeId: null,
          }) as Promise<ChatMessageItem>
        )
      )
    );
  }

  editMessage(messageId: number, text: string, attached?: ChatMessageItem['attached']): Observable<ChatMessageItem> {
    this.requireDialog();
    const payload: Record<string, unknown> = { text: text.trim() };
    if (attached) {
      payload.attached = attached;
    }
    return from(this.usersChat().patch(messageId, payload) as Promise<ChatMessageItem>);
  }

  markRead(messageId: number): Observable<ChatMessageItem> {
    this.requireDialog();
    return from(
      this.usersChat().patch(messageId, { readAt: new Date().toISOString() }) as Promise<ChatMessageItem>
    );
  }

  removeMessage(messageId: number): Observable<ChatMessageItem> {
    this.requireDialog();
    return from(this.usersChat().remove(messageId) as Promise<ChatMessageItem>);
  }

  onMessageCreated(handler: (msg: ChatMessageItem) => void): () => void {
    return this.onMessageEvent('created', handler);
  }

  onMessageRemoved(handler: (msg: ChatMessageItem) => void): () => void {
    return this.onMessageEvent('removed', handler);
  }

  onMessagePatched(handler: (msg: ChatMessageItem) => void): () => void {
    return this.onMessageEvent('patched', handler);
  }

  isUnreadIncoming(msg: ChatMessageItem, currentUserId: number | null): boolean {
    if (!msg || msg.readAt) {
      return false;
    }
    if (msg.senderAddressId) {
      return false;
    }
    if (currentUserId != null && Number(msg.userId) === Number(currentUserId)) {
      return false;
    }
    return String(msg.type || 'support') === 'support';
  }

  private findMessages(supportUserId: number, page: number, limit: number): Observable<ChatMessageItem[]> {
    return from(
      this.usersChat().find({
        query: {
          type: 'support',
          participant: supportUserId,
          $limit: limit,
          $skip: page * limit,
          $sort: { createdAt: -1 },
        },
      }) as Promise<{ data?: ChatMessageItem[] } | ChatMessageItem[]>
    ).pipe(
      map((result) => {
        const data = Array.isArray(result) ? result : result?.data || [];
        return [...data].reverse();
      })
    );
  }

  private createMessageLinks(messages: ChatMessageItem[]): Observable<Array<{ id: number; service: string }>> {
    if (!messages.length) {
      return of([]);
    }

    return forkJoin(
      messages.map((message) => {
        const linkType =
          message.type === 'task-comments' ||
          message.type === 'support' ||
          String(message.type || '').includes('chat')
            ? 'users-chat'
            : 'messages';

        return from(
          this.feathers.service('message-links').create({
            linkId: message.id,
            type: linkType,
          }) as Promise<{ id: number }>
        ).pipe(map((link) => ({ id: link.id, service: 'users-chat' })));
      })
    );
  }

  private onMessageEvent(
    event: 'created' | 'removed' | 'patched',
    handler: (msg: ChatMessageItem) => void
  ): () => void {
    const dialog = this.dialog;
    if (!dialog) {
      return () => undefined;
    }

    const service = this.usersChat();
    const listener = (msg: ChatMessageItem) => {
      if (String(msg?.type || '') === 'support') {
        handler(msg);
      }
    };

    service.on(event, listener);
    return () => {
      try {
        service.removeListener(event, listener);
      } catch {
        /* ignore */
      }
    };
  }

  private usersChat(): any {
    return this.feathers.service('users-chat');
  }

  private requireDialog(): SupportChatDialog {
    if (!this.dialog || this.dialog.type !== 'support') {
      throw new Error('Support chat is not open');
    }
    if (!this.dialog.supportUserId || Number.isNaN(Number(this.dialog.supportUserId))) {
      throw new Error('Invalid support user id');
    }
    return this.dialog;
  }
}
